import * as React from 'react';
import { TextInput, type TextInputProps } from '@mentimeter/ragnar-form';
import { useTranslations } from 'next-intl';
import emailSpellChecker from '@zootools/email-spell-checker';
import { useField } from 'formik';
import { useCallback, useEffect, useState, type FocusEvent } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import type { MailSuggestion } from '@zootools/email-spell-checker/dist/lib/types';
import { Link } from '@mentimeter/ragnar-ui/link';

export const BaseEmailInput = (props: TextInputProps) => {
  const t = useTranslations('authentication.legacy.authPackage');
  const { name, hintText, onBlur } = props;
  const [field, _, fieldHelper] = useField(name);
  const [suggestion, setSuggestion] = useState<MailSuggestion | null>(null);

  useEffect(() => {
    setSuggestion(null);
  }, [field.value]);

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const email = emailSpellChecker.run({
        email: field.value,
      });

      if (!email) {
        onBlur?.(e);
        return;
      }

      setSuggestion(email);
      onBlur?.(e);
    },
    [field.value, onBlur],
  );

  const handleSuggestedChange = useCallback(() => {
    if (!suggestion) {
      return;
    }
    fieldHelper.setValue(suggestion.full);
    setSuggestion(null);
    fieldHelper.setTouched(true);
  }, [suggestion, fieldHelper]);

  return (
    <TextInput
      {...props}
      onBlur={handleBlur}
      hintText={
        suggestion ? (
          <Box className="w-full flex flex-row justify-between flex-wrap gap-2 ">
            <Box className="flex-grow">
              <Text
                as="p"
                className="text-text-negative text-87.5 leading-175"
                fontSize={1}
              >
                {t('form_email_spellcheck', {
                  suggestion: suggestion.domain,
                })}
              </Text>
            </Box>
            <Box>
              <Link
                onClick={handleSuggestedChange}
                fontSize={1}
                className="text-87.5 leading-175"
              >
                {t('form_email_suggestion_button')}
              </Link>
            </Box>
          </Box>
        ) : (
          hintText
        )
      }
      extend={() => ({
        '& :has(#input-feedback-email)': {
          width: '100%',
        },
        '& #input-feedback-email': {
          display: 'block',
          width: '100%',
        },
      })}
    />
  );
};
